<template>
  <div class="dd-level">
    <div class="dd-level__header">
      <div class="dd-level__name">{{ level.name }}</div>
    </div>
    <ul v-if="showEffectLimits && displayGrants.length" class="dd-level__grants">
      <li v-for="(g, idx) in displayGrants" :key="idx">{{ grantText(g) }}</li>
    </ul>
    <div v-if="level.description" class="dd-level__desc">{{ level.description }}</div>
    <div class="dd-level__footer">
      <div class="dd-level__pricecol">
        <div class="dd-level__price-row">
          <div class="dd-level__price-main">{{ level.price }} {{ currencySymbol }}</div>
          <div class="dd-level__price-right">
            <div class="dd-level__price-period">{{ $t('wall.subscription.per_month') }}</div>
            <div class="dd-level__discount-text" :class="{ 'is-empty': !discountText }">
              {{ discountText }}
            </div>
          </div>
        </div>
      </div>
      <div class="dd-level__cta">
        <template v-if="authenticated">
          <button v-if="isCurrent" class="btn btn--disabled" disabled>{{ $t('wall.subscription.current_level') }}</button>
          <button v-else :disabled="ctaDisabled" :class="ctaClass" @click="onSubscribe">{{ ctaText }}</button>
        </template>
      </div>
    </div>
  </div>

</template>

<script>
import SubscriptionPaymentMethodStatus from "@/store/models/SubscriptionPaymentMethodStatus";

export default {
  name: 'LevelCard',
  props: {
    level: { type: Object, required: true },
    currentLevel: { type: Object, default: null },
  },
  computed: {
    samePriceEpsilon() { return 0.000001; },
    isCurrent() { return this.currentLevel && this.currentLevel.externalID === this.level.externalID; },
    busy() { return this.$store.getters.subscriptionRequestInProgress; },
    authenticated() { return this.$store.getters.authenticated; },
    showEffectLimits() {
      return this.level.showEffectLimits !== false;
    },
    displayGrants() {
      const grants = Array.isArray(this.level.grants) ? [...this.level.grants] : [];
      if (this.level.freeGenerationSessions > 0) {
        grants.push({
          limit: this.level.freeGenerationSessions,
          period: this.level.freeGenerationPeriod,
          is_generation: true,
        });
      }
      return grants;
    },
    paymentMethodStatus() {
      return this.$store.getters.subscriptionPaymentMethodStatus;
    },
    priceDelta() {
      if (!this.currentLevel) {
        return 0;
      }
      return Number(this.level.pricePerYearInUSD || 0) - Number(this.currentLevel.pricePerYearInUSD || 0);
    },
    isMoreExpensive() {
      return !!this.currentLevel && this.priceDelta > this.samePriceEpsilon;
    },
    isCheaper() {
      return !!this.currentLevel && this.priceDelta < -this.samePriceEpsilon;
    },
    isSamePrice() {
      return !!this.currentLevel && !this.isCurrent && Math.abs(this.priceDelta) <= this.samePriceEpsilon;
    },
    isScheduledNext() {
      const next = this.$store.getters.nextLevel;
      return !!(next && next.externalID === this.level.externalID);
    },
    downgradeBlocked() {
      return (
        !!this.currentLevel &&
        this.isCheaper &&
        this.paymentMethodStatus === SubscriptionPaymentMethodStatus.NonRecurrent
      );
    },
    ctaText() {
      if (!this.currentLevel) return this.$t('wall.subscription.subscribe');
      if (this.isScheduledNext) return this.$t('subscriptions.cancel_transition');
      if (this.isSamePrice) return this.$t('wall.subscription.switch_level');
      return this.isMoreExpensive ? this.$t('wall.subscription.upgrade') : this.$t('wall.subscription.schedule_downgrade');
    },
    ctaDisabled() {
      return this.busy || this.downgradeBlocked;
    },
    ctaClass() {
      if (this.isScheduledNext) {
        return 'btn btn--outline';
      }
      if (this.downgradeBlocked) {
        return 'btn btn--disabled';
      }
      return 'btn';
    },
    discountValue() {
      if (!this.$store.getters.subscriptionDiscountsEnabled) return 0;
      const discounts = [this.level.discount3m, this.level.discount6m, this.level.discount12m]
        .map(v => Number(v) || 0)
        .filter(v => v > 0);
      if (!discounts.length) return 0;
      return Math.max(...discounts);
    },
    discountText() {
      const d = this.discountValue;
      if (!d) return '';
      // Pick the largest period having discount to display
      let months = 0;
      if (Number(this.level.discount12m) === d) months = 12;
      else if (Number(this.level.discount6m) === d) months = 6;
      else if (Number(this.level.discount3m) === d) months = 3;
      return this.$t('wall.subscription.discount_hint', { percent: d, months });
    },
    currencySymbol() {
      const map = { USD: '$', EUR: '€', RUB: '₽' };
      return map[this.level.currency] || this.level.currency;
    },
  },
  methods: {
    grantText(g) {
      const period = g.period ? this.$t(`wall.subscription.grant_period.${g.period}`) : '';
      if (g.is_generation) {
        const label = this.$t('sponsor.ai_sticker_creation');
        return `${label} — ${g.limit} ${period}`.trim();
      }
      if (g.class_name) {
        return this.$t('wall.subscription.grant_with_class', { limit: g.limit, class: g.class_name, period }).trim();
      }
      return this.$t('wall.subscription.grant_plain', { limit: g.limit, period }).trim();
    },
    onSubscribe() {
      if (this.ctaDisabled) return;
      if (this.isScheduledNext && this.currentLevel?.ID) {
        // Cancel planned downgrade by re-subscribing to the current level (legacy-compatible)
        this.$store.dispatch('subscribe', this.currentLevel.ID);
      } else {
        this.$emit('subscribe', this.level);
      }
    }
  }
}
</script>

<style scoped>
.dd-level {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dd-level__header { display: flex; justify-content: space-between; }
.dd-level__name { overflow-wrap: anywhere; }
.dd-level__desc { overflow-wrap: anywhere; }
.dd-level__grants li { overflow-wrap: anywhere; }
.dd-level__footer {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 12px;
  margin-top: auto;
  padding-top: 8px;
}
.dd-level__pricecol {
  flex: 1 1 auto;
  display: flex;
}
.dd-level__price-row {
  display: flex;
  align-items: stretch;
  gap: 4px;
  width: 100%;
}
.dd-level__price-main {
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  display: flex;
  align-items: center;
  align-self: stretch;
  min-height: 30px;
  text-wrap: nowrap;
}
.dd-level__price-right {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  min-height: 32px;
  gap: 0;
}
.dd-level__price-period {
  font-size: 10px;
  text-transform: uppercase;
  color: #666;
  font-weight: 600;
  line-height: 1;
  display: flex;
  align-items: center;
  flex: 1;
}
.dd-level__discount-text {
  color: var(--dd-accent);
  font-size: 12px;
  font-weight: 500;
  min-height: 12px;
  flex: 1;
  display: flex;
  align-items: center;
}
.dd-level__discount-text.is-empty { visibility: hidden; }
.dd-level__cta { display: flex; gap: 8px; margin-left: auto; align-items: center; }
</style>
